import { TreeNode } from 'rsuite/esm/internals/Tree/types';

interface LinearNodes {
  Id: number;
  Parent: number;
  LabelText: string;
  Ctype?: string;
  SortedOrder?: number;
  MasterParentId?: number;
}

const getTreeKey = (id: number, ctype?: string): string => {
  return ctype ? `${ctype.toLowerCase()}:${id}` : `${id}`;
};

function linearToTree(linear: LinearNodes[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  const sortedOrderMap = new Map<string, number>();

  const rootNodes: TreeNode[] = [];


  // ✅ Step 1: Create all nodes first
  linear.forEach((item) => {
    const uniqueKey = getTreeKey(item.Id, item.Ctype);
    const node: TreeNode = {
      label: item.LabelText,
      value: uniqueKey,
    };
    map.set(uniqueKey, node);
    if (item.SortedOrder !== undefined) {
      sortedOrderMap.set(uniqueKey, item.SortedOrder);
    }
  });

  // ✅ Step 2: Build tree structure
  linear.forEach((item) => {
    const uniqueKey = getTreeKey(item.Id, item.Ctype);
    const node = map.get(uniqueKey)!;

    if (item.Parent === 0) {
      rootNodes.push(node);
    } else {
      // Find parent using the parent's unique key
      const parentItem = linear.find((n) => n.Id === item.Parent && n.Ctype);
      const parentKey = parentItem
        ? getTreeKey(parentItem.Id, parentItem.Ctype)
        : `${item.Parent}`;

      const parentNode = map.get(parentKey);
      if (parentNode) {
        if (!parentNode.children) {
          parentNode.children = [];
        }
        node.parent = { label: parentNode.label, value: parentNode.value };
        parentNode.children.push(node);
      }
    }
  });

  // ✅ Step 3: Sort children by SortedOrder (if available)
  const sortChildren = (nodes: TreeNode[]) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 1) {
        node.children.sort((a, b) => {
          const orderA = sortedOrderMap.get(String(a.value)) ?? 0;
          const orderB = sortedOrderMap.get(String(b.value)) ?? 0;
          return orderA - orderB;
        });
        // Recursively sort children
        sortChildren(node.children);
      }
    });
  };

  sortChildren(rootNodes);

  return rootNodes;
}


type CourseBuilderItems = 'chapter' | 'section' | 'subject' | 'pdf' | 'video' | 'mcq' | 'book' | 'backup' | 'podcast' | 'live' | 'testseries'

const getInitialExpandedItems = (linearData: LinearNodes[], action: CourseBuilderItems[]): string[] => {
  const t: string[] = [];
  for (let d of linearData) {
    const cType = d.Ctype?.toLowerCase();

    if (action.includes(cType as CourseBuilderItems)) {
      t.push(getTreeKey(d.Id, d.Ctype));
    }
  }
  return t;
}

export default linearToTree;
export type { LinearNodes };
export { getTreeKey, getInitialExpandedItems };
